import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import apiService from '../../../services/api';
import { rotuloTipoAtividade } from '../../../constants/tipoAtividade';
import { formatDate } from '../../../utils/formatters';
import BadgeInstituicao from './BadgeInstituicao';
import { STATUS_LEGENDA } from './constantes';
import { Mensagem } from './estilos';

const Faixa = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  background: #f2f0f0;
  border-bottom: 1px solid #dfe3e8;
  font-family: 'Public Sans', sans-serif;
`;

const Area = styled.h2`
  margin: 0;
  padding: 12px 40px;
  font-size: 20px;
  font-weight: 700;
  color: #6ab8a8;
  text-transform: uppercase;
`;

const Vagas = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 28px;
  border-left: 2px solid #dfe3e8;
  font-size: 14px;
  font-weight: 700;
  color: #6ab8a8;

  strong {
    color: #ff9f1f;
  }
`;

const Corpo = styled.div`
  max-width: 640px;
  padding: 16px 0 32px;
  font-family: 'Public Sans', sans-serif;
`;

const Unidade = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 40px 12px;
  font-size: 16px;
  color: #6ab8a8;
  font-weight: 600;

  span {
    color: #555;
  }
`;

const Linha = styled.div`
  display: grid;
  grid-template-columns: 230px 1fr;
  padding: 10px 40px;
  border-bottom: 1px solid #dfe3e8;
  font-size: 15px;
  background: ${({ $destaque }) => ($destaque ? '#e8f5f3' : '#ffffff')};

  dt {
    font-weight: 700;
    color: #002e3b;
  }

  dd {
    margin: 0;
    color: #555;
    white-space: pre-line;
  }
`;

const Rodape = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 32px;
`;

const BotaoVoltar = styled.button`
  display: flex;
  align-items: center;
  gap: 16px;
  background: #6ab8a8;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 28px 8px 16px;
  font-size: 18px;
  box-shadow: 0 4px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
`;

const periodoFormatado = (periodos) =>
  periodos.length
    ? periodos.map((periodo) => `${formatDate(periodo.dataInicio)} - ${formatDate(periodo.dataFim)}`).join('\n')
    : '-';

const camposDoDetalhe = (solicitacao) => [
  ['Instituição', solicitacao.instituicao ? `${solicitacao.instituicao.nome} (${solicitacao.instituicao.sigla})` : '-'],
  ['Curso', solicitacao.curso],
  ['Disciplina', solicitacao.nomeDisciplina],
  ['Especialidade', solicitacao.especialidade],
  ['Setor', solicitacao.setor.nome],
  ['Tipo', rotuloTipoAtividade(solicitacao.tipoAtividade)],
  ['Status', STATUS_LEGENDA[solicitacao.status]?.rotulo ?? solicitacao.status],
  ['Período', periodoFormatado(solicitacao.periodos)],
  ['Turno', solicitacao.turno],
  ['Dias da semana', solicitacao.diasSemana.length ? solicitacao.diasSemana.join(', ') : '-'],
  ['Nº de alunos solicitados', solicitacao.quantidadeAlunosSolicitados],
  ...(solicitacao.quantidadeAlunosConcedidos !== null
    ? [['Nº de alunos concedidos', solicitacao.quantidadeAlunosConcedidos]]
    : []),
  ['Nº de horas / aluno', `${solicitacao.horasPorAluno}h`],
  ['Nº total de horas', `${solicitacao.totalHoras}h`],
  ...(solicitacao.motivoNegacao ? [['Motivo da negação', solicitacao.motivoNegacao]] : []),
];

const DetalheRelatorio = ({ relatorio, solicitacaoId, onVoltar }) => {
  const [solicitacao, setSolicitacao] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    let ativo = true;
    setSolicitacao(null);
    setErro(null);
    apiService
      .getRelatorioConcessaoDetalhe(relatorio, solicitacaoId)
      .then((resposta) => ativo && setSolicitacao(resposta.solicitacao))
      .catch((falha) => ativo && setErro(falha.message || 'Falha ao carregar a solicitação.'));
    return () => {
      ativo = false;
    };
  }, [relatorio, solicitacaoId]);

  if (erro) return <Mensagem>{erro}</Mensagem>;
  if (!solicitacao) return <Mensagem>Carregando...</Mensagem>;

  return (
    <>
      <Faixa>
        <Area>{solicitacao.area.nome}</Area>
        <Vagas>
          VAGAS
          <strong>{solicitacao.vagasOfertadas}</strong>
        </Vagas>
      </Faixa>
      <Corpo>
        <Unidade>
          <BadgeInstituicao instituicao={solicitacao.instituicao} />
          Unidade de Saúde: <span>{solicitacao.estabelecimento}</span>
        </Unidade>
        <dl style={{ margin: 0 }}>
          {camposDoDetalhe(solicitacao).map(([rotulo, valor], indice) => (
            <Linha key={rotulo} $destaque={indice % 2 === 0}>
              <dt>{rotulo}:</dt>
              <dd>{valor}</dd>
            </Linha>
          ))}
        </dl>
        <Rodape>
          <BotaoVoltar type="button" onClick={onVoltar}>
            <span className="material-symbols-outlined">chevron_left</span>
            Voltar
          </BotaoVoltar>
        </Rodape>
      </Corpo>
    </>
  );
};

export default DetalheRelatorio;
